import { assert, DefaultMap, definedMap } from "@glideapps/ts-necessities";
import { isLeft } from "fp-ts/lib/Either";
import * as iots from "io-ts";
import { convertDateFromTimeZoneAgnostic } from "../date-time/support";
import { asDate, asMaybeBoolean, asMaybeNumber, asMaybeString } from "./components/component-helpers";
import { isPrimitiveValue } from "./components/computation-types";
import { logError } from "./debug-print";
import type { ColumnTypeKind, StringGlideTypeKind } from "./description";
import { isDateTimeTypeKind, isStringTypeKind } from "./description";
import { isArray } from "./Support";
import type { YesCodeColumnManifest, YesCodeType, YesCodeValue } from "./yes-code-types";
import { yesCodeColumnManifest } from "./yes-code-types";

const yesCodeManifest = iots.union([yesCodeColumnManifest, iots.readonlyArray(yesCodeColumnManifest)]);

const manifestPromises = new DefaultMap<string, Promise<YesCodeColumnManifest | string>>(fetchManifest);
const manifestResults = new Map<string, YesCodeColumnManifest | string>();

export function normalizeYesCodeURL(url: string): string {
    if (!url.includes("://")) {
        url = "https://" + url;
    }
    if (!url.endsWith("/")) {
        url = url + "/";
    }
    return url;
}

async function fetchManifest(url: string): Promise<YesCodeColumnManifest | string> {
    url = normalizeYesCodeURL(url);

    let manifestURL = url + "glide.json";
    let result: Response;
    try {
        result = await fetch(manifestURL);
        if (!result.ok) throw new Error("fail");
    } catch {
        manifestURL = url + "manifest.json";
        try {
            result = await fetch(manifestURL);
            if (!result.ok) throw new Error("fail");
        } catch {
            return 'Loading the manifest "glide.json" failed. Is the URL correct, and the hosting set up correctly?';
        }
    }

    let json: unknown;
    try {
        json = await result.json();
    } catch {
        return "The manifest is not correct JSON.";
    }

    const parsed = yesCodeManifest.decode(json);
    if (isLeft(parsed)) return "The manifest does not have the correct format.";

    if (isArray(parsed.right)) {
        const manifest = parsed.right[0];
        if (manifest === undefined) {
            return "The manifest must contain at least one column.";
        }
        return manifest;
    } else {
        return parsed.right;
    }
}

async function getYesCodeManifestAsync(url: string): Promise<YesCodeColumnManifest | string> {
    try {
        const result = await manifestPromises.get(url);
        manifestResults.set(url, result);
        if (typeof result === "string") {
            manifestPromises.delete(url);
        }
        return result;
    } catch (e: unknown) {
        logError("Could not fetch manifest", e);
        manifestPromises.delete(url);
        return "An unexpected error happened when loading the manifest.";
    }
}

export function getYesCodeManifest(
    url: string
): YesCodeColumnManifest | string | Promise<YesCodeColumnManifest | string> {
    const maybeResult = manifestResults.get(url);
    if (maybeResult !== undefined) {
        return maybeResult;
    }

    return getYesCodeManifestAsync(url);
}

export function unloadYesCodeManifest(url: string): void {
    manifestPromises.delete(url);
}

function isStringType(type: YesCodeType): type is StringGlideTypeKind {
    return isStringTypeKind(type as ColumnTypeKind);
}

export function convertYesCodeValue(
    value: unknown,
    type: YesCodeType
): { value: YesCodeValue | undefined; type: YesCodeType } {
    function convertString(v: unknown): string | undefined {
        const s = asMaybeString(v);
        if (s === "") return undefined;
        return s;
    }

    if (value === undefined) {
        return { value: undefined, type };
    } else if (isDateTimeTypeKind(type as ColumnTypeKind)) {
        return { value: definedMap(asDate(value), d => convertDateFromTimeZoneAgnostic(d).toISOString()), type };
    } else if (isStringType(type)) {
        return { value: convertString(value), type: "string" };
    } else if (type === "number") {
        return { value: asMaybeNumber(value), type: "number" };
    } else if (type === "boolean") {
        return { value: asMaybeBoolean(value), type: "boolean" };
    } else if (type === "primitive") {
        if (typeof value === "string") {
            return { value, type: "string" };
        } else if (typeof value === "number") {
            return { value, type: "number" };
        } else if (typeof value === "boolean") {
            return { value, type: "boolean" };
        } else if (isPrimitiveValue(value)) {
            return { value: convertString(value), type: "string" };
        } else {
            return { value: undefined, type: "primitive" };
        }
    } else {
        if (!isArray(value)) return { value: undefined, type };

        return {
            value: value.map(v => {
                const r = convertYesCodeValue(v, type.items).value;
                assert(!isArray(r));
                return r;
            }),
            type,
        };
    }
}
